<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="mb-4">
          <h4 class="mb-1">Lista de usuarios</h4>
          <p class="text-muted mb-0">Consulta y gestión de usuarios del sistema</p>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-md-6">
                <label class="col-form-label mr-2">Buscar:</label>
                <input v-model="searchQuery" type="text" class="form-control d-inline-block" style="max-width: 300px"
                  placeholder="Buscar por ID, nombre, email" />
              </div>
            </div>
            <div v-if="loading" class="text-center py-5">
              <div class="loader-box">
                <div class="loader-3"></div>
              </div>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover table-striped">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in paginatedUsers" :key="user.uid">
                    <td>{{ user.name || '—' }}</td>
                    <td>{{ user.email || '—' }}</td>

                    <td>
                      <span v-if="user.rol" class="badge bg-primary">{{ user.rol }}</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>
                      <span :class="[
                        'badge',
                        user.status === 'active' ? 'bg-success' : 'bg-danger',
                      ]">
                        {{ user.status === 'active' ? 'Activo' : 'Inactivo' }}
                      </span>
                    </td>
                    <td>
                      <div class="btn-group btn-group-sm">
                        <button type="button" class="btn btn-info" title="Copiar" @click="copyUser(user)">
                          <vue-feather type="copy" size="14" stroke-width="2" />
                        </button>
                        <button type="button" class="btn btn-warning" title="Enviar email" @click="sendEmail(user)">
                          <vue-feather type="mail" size="14" stroke-width="2" />
                        </button>
                        <button type="button" class="btn btn-primary" title="Editar" @click="editUser(user)">
                          <vue-feather type="edit-2" size="14" stroke-width="2" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!loading && filteredUsers.length === 0">
                    <td colspan="6" class="text-center text-muted py-4">
                      No hay usuarios
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="totalPages > 1" class="d-flex justify-content-end align-items-center mt-3">
              <nav>
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" :class="{ disabled: currentPage === 1 }">
                    <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">
                      «
                    </a>
                  </li>
                  <li v-for="p in pageNumbers" :key="p" class="page-item" :class="{ active: p === currentPage }">
                    <a class="page-link" href="#" @click.prevent="goToPage(p)">{{ p }}</a>
                  </li>
                  <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                    <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">
                      »
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { usersApi } from '@/services/api';

const ITEMS_PER_PAGE = 10;

export default {
  name: 'ConsultaUsuariosPage',
  data() {
    return {
      users: [],
      loading: false,
      searchQuery: '',
      currentPage: 1,
    };
  },
  computed: {
    filteredUsers() {
      if (!this.searchQuery.trim()) return this.users;
      const q = this.searchQuery.trim().toLowerCase();
      return this.users.filter(
        (u) =>
          (u.uid && u.uid.toLowerCase().includes(q)) ||
          (u.name && u.name.toLowerCase().includes(q)) ||
          (u.email && u.email.toLowerCase().includes(q))
      );
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredUsers.length / ITEMS_PER_PAGE));
    },
    pageNumbers() {
      const pages = [];
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
      return pages;
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * ITEMS_PER_PAGE;
      return this.filteredUsers.slice(start, start + ITEMS_PER_PAGE);
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.loadUsers();
  },
  methods: {
    async loadUsers() {
      this.loading = true;
      try {
        this.users = await usersApi.getAll();
        this.currentPage = 1;
      } catch (err) {
        this.$toast?.show?.(err.message || 'Error al cargar usuarios', {
          type: 'error',
          position: 'top-right',
        });
        this.users = [];
      } finally {
        this.loading = false;
      }
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    copyUser(user) {
      const text = `${user.name || ''}\t${user.email || ''}\t${user.uid || ''}`;
      navigator.clipboard?.writeText(text).then(() => {
        this.$toast?.show?.('Usuario copiado al portapapeles', {
          type: 'success',
          position: 'top-right',
        });
      });
    },
    sendEmail(user) {
      if (user.email) {
        window.location.href = `mailto:${user.email}`;
      } else {
        this.$toast?.show?.('El usuario no tiene email', { type: 'warning', position: 'top-right' });
      }
    },
    editUser(user) {
      // TODO: implementar edición (modal o ruta)
      this.$toast?.show?.(`Editar usuario: ${user.name || user.email}`, {
        type: 'info',
        position: 'top-right',
      });
    },
  },
};
</script>
